import { assert } from '@sindresorhus/is';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import type { VideoJsPlayer } from 'video.js';
import { PlayerEvents } from './plugins';
import type { EventTrackingOptions } from './plugins';
import { StyledVideo, videojs } from './videojs-setup';

const PLACEHOLDER = '/assets/placeholder.mp4';

const StyledWrapper = styled.div`
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }

  /**
   * This is to make sure, the background is delegated to
   * the player rather than video when showing big play button.
   */
  &.transparent {
    > .video-js {
      background-color: rgba(0, 0, 0, 0);

      > video {
        opacity: 0;
      }
    }
  }
`;

interface VideoJsPlayerWithPlugins extends VideoJsPlayer {
  EventTracking: (options?: EventTrackingOptions) => void;
  mux: (config: Record<string, unknown>) => void;
  VttThumbnails: () => {
    update: (options: { src: string; width?: number }) => void;
  };
  Playlist: () => {
    update: (
      options: { src: string[]; isLive?: boolean; ui?: boolean },
      onChange?: (index: number) => void
    ) => void;
  };
  hlsQualitySelector: (config: { displayCurrentQuality?: boolean }) => void;
}

interface VideoProps {
  style?: React.CSSProperties;
  className?: string;
  streamUrl?: string;
  isLive?: boolean;
  onPlay?: (isAutoplay: boolean) => void;
  onInitialPlay?: (isAutoplay: boolean) => void;
  onSeek?: () => void;

  /**
   * Triggers when user clicks on the "live" button
   * Triggers only on live events
   */
  onLiveSeek?: () => void;

  /**
   * Updates every second
   *
   * @param currentTime - current time in seconds
   * @param totalTime - total time in seconds
   */
  onCurrentTimeUpdate?: (currentTime: number, totalTime: number) => void;

  onError?: (error?: MediaError) => void;
}

export const Video = ({
  streamUrl,
  isLive,
  style,
  className,
  onPlay,
  onSeek,
  onLiveSeek,
  onCurrentTimeUpdate,
  onInitialPlay,
  onError
}: VideoProps): React.ReactElement => {
  const videoElement = useRef<HTMLVideoElement>(null);
  const videoJsPlayer = useRef<VideoJsPlayerWithPlugins>();
  const [canPlay, setCanPlay] = useState(false);

  const handleOnPlay = useCallback(
    async (args?: { autoplay: boolean }) => {
      const player = videoJsPlayer.current;

      if (!player) {
        return;
      }

      try {
        /**
         * Even when we get onplay event, we need to cross check once
         * again if video can be played using play method from video.js
         */
        await player.play();

        if (!canPlay) {
          setCanPlay(true);
          onInitialPlay?.(!!args?.autoplay);
          player.addClass('vjs-can-play');
        } else {
          onPlay?.(!!args?.autoplay);
        }
      } catch {
        // Ignore
      }
    },
    [canPlay, onInitialPlay, onPlay]
  );

  const onPlayerReady = useCallback(async () => {
    const player = videoJsPlayer.current;

    if (!player) {
      return;
    }

    // For IOS. To show inline player without going full screen by default.
    player.playsinline(true);

    // unregister all custom player events
    player.off(Object.values(PlayerEvents));

    // try autoplay.
    await handleOnPlay({ autoplay: true });

    // register custom player events
    player.on(PlayerEvents.PLAY, () => {
      void handleOnPlay();
    });

    player.on(PlayerEvents.SEEK, (_, { isLive: live }) => {
      if (live) {
        onLiveSeek?.();
      } else {
        onSeek?.();
      }
    });

    player.on(PlayerEvents.ERROR, (_, { error }) => onError?.(error));
  }, [handleOnPlay, onError, onLiveSeek, onSeek]);

  const getPlayer = useCallback(() => {
    let player = videoJsPlayer.current;

    if (!player) {
      assert.domElement(videoElement.current);

      player = videojs(videoElement.current, {
        liveui: true,
        defaultVolume: 1,
        muted: true,
        userActions: {
          hotkeys: true
        },
        controlBar: {
          children: [
            'LiveTracker',
            'SeekToLive',
            'PlayToggle',
            'ProgressControl',
            'TimeDisplay',
            'volumePanel',
            'FullscreenToggle'
          ]
        },
        controls: true,
        techOrder: ['html5'] // Required
      }) as VideoJsPlayerWithPlugins;

      assert.object(player);

      videoJsPlayer.current = player;

      player.EventTracking();
    }

    return player;
  }, []);

  // Cleanup player on unmount
  const disposePlayer = useCallback(
    () => (): void => {
      // Dispose of the current VideoJS player
      if (videoJsPlayer.current) {
        videoJsPlayer.current.dispose();
        videoJsPlayer.current = undefined;
      }
    },
    []
  );

  const [currentSource, setCurrentSource] = useState<string>();

  const loadPlaceholder = useCallback(() => {
    if (currentSource === PLACEHOLDER) {
      return;
    }

    disposePlayer();

    const player = getPlayer();

    player.src(PLACEHOLDER);
    setCurrentSource(PLACEHOLDER);
    player.loop(true);
    player.load();

    player.ready(onPlayerReady);
  }, [currentSource, disposePlayer, getPlayer, onPlayerReady]);

  const loadSource = useCallback(() => {
    if (!streamUrl) {
      return;
    }

    setCurrentSource(streamUrl);
    disposePlayer();

    const player = getPlayer();

    player.Playlist().update({
      src: [streamUrl],
      isLive,
      ui: false
    });

    player.loop(false);

    player.ready(onPlayerReady);
  }, [disposePlayer, getPlayer, isLive, onPlayerReady, streamUrl]);

  useEffect(() => {
    if (streamUrl?.length) {
      loadSource();
    } else {
      loadPlaceholder();
    }
  }, [loadPlaceholder, loadSource, streamUrl]);

  const intervalRef = useRef<number>();

  // Send currentTimeUpdate every second
  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      const player = videoJsPlayer.current;

      if (!player) {
        return;
      }

      const currentTime = player.currentTime();
      const duration = player.duration();

      if (!currentTime || !duration) {
        return;
      }

      onCurrentTimeUpdate?.(currentTime, duration);
    }, 1000); // every second
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    // Cleanup player on unmount
    useEffect(
      () => (): void => {
        // Dispose the current VideoJS player
        if (videoJsPlayer.current) {
          videoJsPlayer.current.dispose();
          videoJsPlayer.current = undefined;
        }

        // Dispose interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      },
      []
    );

  return (
    <StyledWrapper
      className={cx(className, {
        'hidden': canPlay && !streamUrl?.length,
        'transparent': !canPlay,
        'vjs-can-play': !canPlay
      })}
    >
      <StyledVideo
        ref={videoElement}
        style={{ ...style }}
        controls
        className={cx('video-js vjs-big-play-centered')}
      />
    </StyledWrapper>
  );
};
